import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from './Sidebar';

// The shell every "How it all works" explainer renders in.
//
// Extracted 2026-08-14 from DocumentsInfo, which was the first of these. Three
// more were asked for (Suppliers, Briefs, Compliance) and copying ninety lines
// of scroll-spy and chrome four times is how four pages quietly stop looking
// like each other — the same fault as the two email chassis that drifted for
// months. One shell, four content files.
//
// Staff-facing, so it lives in the ADMIN chrome (Sidebar + content), not the
// supplier HelpArticleLayout wrapper with its "Back to portal" and support
// footer. It still reuses the help KIT (StatusCards, LifecycleStepper,
// Comparison, InfoCallout) inside section bodies.
//
// Props:
//   sections   [{ id, title, icon, body }] — body is JSX
//   title      the h1 (defaults to "How it all works")
//   intro      one paragraph under the h1
//   backTo     route the "← Back to X" link goes to
//   backLabel  what that link says
//   adminOnly  refuse a direct URL hit from a non-admin (Compliance, Documents)
export default function HowItWorksPage({
  sections = [],
  title = 'How it all works',
  intro,
  backTo,
  backLabel,
  adminOnly = false,
  restrictedNote = 'This page is restricted to Admin and Super User accounts.',
}) {
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [active, setActive] = useState(sections[0]?.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-15% 0px -75% 0px', threshold: 0 });
    // Captured into a local so the cleanup disconnects the observer this effect
    // created, not whatever the ref points at by then.
    const refs = sectionRefs.current;
    Object.values(refs).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [sections]);

  const jumpTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActive(id); }
  };

  // A direct URL hit must refuse, not just a hidden nav item.
  if (adminOnly && currentInternalUser && !isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentInternalUser} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="font-['Metropolis'] text-[10px] font-bold tracking-[3px] uppercase text-[#7A4E10] mb-3">Restricted</p>
          <p className="font-['Metropolis'] text-lg text-ink mb-6 max-w-md">{restrictedNote}</p>
          <button onClick={() => navigate('/dashboard')} className="font-['Metropolis'] text-xs font-semibold tracking-[2px] uppercase text-fusion-forest border border-[#92D400] rounded-full px-5 py-2 hover:bg-active-green/10 transition-colors">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="flex-1 flex flex-col min-w-0">
        <div className="pt-8">
          <div className="px-8 mb-6">
            {backTo && (
              <button
                type="button"
                onClick={() => navigate(backTo)}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey hover:text-fusion-forest transition-colors bg-transparent border-0 p-0 cursor-pointer mb-3">
                ← Back to {backLabel}
              </button>
            )}
            <h1 className="fusion-h1 text-fusion-forest">{title}</h1>
            {intro && (
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2 max-w-[68ch]">{intro}</p>
            )}
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        <div className="px-8 pt-6 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5 lg:gap-8 items-start max-w-[1100px]">
            {/* Contents nav — sticky, scroll-spy */}
            <nav aria-label="On this page" className="hidden lg:block lg:sticky lg:top-6">
              <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#009159] mb-3">On this page</div>
              <ul className="space-y-1">
                {sections.map(s => {
                  const isActive = active === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => jumpTo(s.id)}
                        className={`w-full text-left font-['Metropolis'] text-[13px] leading-snug pl-3 py-1.5 border-l-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest ${
                          isActive ? 'border-[#92D400] text-[#009159] font-bold' : 'border-transparent text-[#51534A] hover:text-fusion-mid-grey hover:border-[#D4D9C6]'
                        }`}>
                        {s.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Section cards */}
            <div className="min-w-0 space-y-5">
              {sections.map(s => (
                <section
                  key={s.id}
                  id={s.id}
                  ref={el => { sectionRefs.current[s.id] = el; }}
                  className="scroll-mt-6 bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 sm:p-7"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-8 rounded-lg bg-[#009159]/[0.10] flex items-center justify-center flex-shrink-0 text-[#009159]" aria-hidden="true">{s.icon}</span>
                    <h2 className="font-['Metropolis'] text-[18px] font-bold text-ink tracking-tight leading-tight">{s.title}</h2>
                  </div>
                  <div className="font-['Metropolis'] text-[14px] text-[#4C4E42] leading-[1.65] max-w-[64ch] space-y-3">{s.body}</div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
